import React, { useState, useEffect } from 'react';
import { Gift, Plus, Search, Trash2, Edit, Loader2, Tag, Percent, AlertCircle, CheckCircle } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const CouponManagement = ({ authToken }) => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({
        code: '',
        coupon_name: '',
        discount_type: 'percent',
        value: '',
        expiry: '',
        is_active: true,
        minimum_order_amount: 0,
        maximum_discount_amount: null,
        usage_limit: null
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/admin/super/coupons`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (response.ok) {
                const data = await response.json();
                // API returns bare array or { coupons: [] }
                setCoupons(Array.isArray(data) ? data : (data.coupons || []));
            } else if (response.status === 404) {
                setCoupons([
                    { id: 1, code: 'WELCOME10', coupon_name: 'WELCOME10', discount_type: 'percent', value: 10, expiry: '2024-12-31', is_active: true, minimum_order_amount: 0, maximum_discount_amount: null, usage_limit: null },
                    { id: 2, code: 'FESTIVE500', coupon_name: 'FESTIVE500', discount_type: 'fixed_amount', value: 500, expiry: '2024-03-31', is_active: false, minimum_order_amount: 1000, maximum_discount_amount: null, usage_limit: 100 },
                ]);
            }
        } catch (err) {
            console.error(err);
            setError('Failed to fetch coupons');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, [authToken]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');

        const url = modal?.id ? `${API_BASE_URL}/admin/super/coupons/${modal.id}` : `${API_BASE_URL}/admin/super/coupons`;
        const method = modal?.id ? 'PUT' : 'POST';

        // Prepare body according to API (numeric values where expected)
        const payload = {
            ...form,
            value: parseFloat(form.value) || 0,
            minimum_order_amount: parseFloat(form.minimum_order_amount) || 0,
            maximum_discount_amount: form.maximum_discount_amount ? parseFloat(form.maximum_discount_amount) : null,
            usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null,
            is_active: form.is_active ? 1 : 0
        };

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setModal(null);
                setForm({
                    code: '',
                    coupon_name: '',
                    discount_type: 'percent',
                    value: '',
                    expiry: '',
                    is_active: true,
                    minimum_order_amount: 0,
                    maximum_discount_amount: null,
                    usage_limit: null
                });
                fetchCoupons();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to save coupon');
            }
        } catch (err) {
            setError('Network error. Failed to save coupon.');
        } finally {
            setSaving(false);
        }
    };

    const deleteCoupon = async (id) => {
        if (!window.confirm('Delete this coupon?')) return;
        try {
            const response = await fetch(`${API_BASE_URL}/admin/super/coupons/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (response.ok) {
                fetchCoupons();
            } else {
                setError('Failed to delete coupon');
            }
        } catch (err) {
            setError('Network error. Failed to delete.');
        }
    };

    const openEdit = (coupon) => {
        setModal(coupon);
        setForm({
            code: coupon.code,
            coupon_name: coupon.coupon_name || coupon.code,
            discount_type: coupon.discount_type,
            value: coupon.value,
            expiry: coupon.expiry ? coupon.expiry.slice(0, 10) : '',
            is_active: coupon.is_active === 1 || coupon.is_active === true,
            minimum_order_amount: coupon.minimum_order_amount || 0,
            maximum_discount_amount: coupon.maximum_discount_amount,
            usage_limit: coupon.usage_limit
        });
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Coupon Management</h2>
                <button
                    onClick={() => {
                        setModal({});
                        setForm({
                            code: '',
                            coupon_name: '',
                            discount_type: 'percent',
                            value: '',
                            expiry: '',
                            is_active: true,
                            minimum_order_amount: 0,
                            maximum_discount_amount: null,
                            usage_limit: null
                        });
                    }}
                    className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                    <Plus className="h-4 w-4" />
                    <span>Create Coupon</span>
                </button>
            </div>

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow border overflow-hidden">
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {coupons.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">No coupons found.</td>
                                </tr>
                            ) : (
                                coupons.map((coupon) => (
                                    <tr key={coupon.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center font-mono font-bold text-indigo-600">
                                                <Tag className="h-4 w-4 mr-2 text-gray-400 font-normal" />
                                                {coupon.code}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {coupon.coupon_name || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {coupon.discount_type === 'percent' || coupon.discount_type === 'percentage' ? `${coupon.value}% Off` : (coupon.discount_type === 'free_shipping' ? 'Free Shipping' : `₹${coupon.value} Off`)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-sm">
                                            {coupon.expiry ? new Date(coupon.expiry).toLocaleDateString() : 'No expiry'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                {coupon.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button onClick={() => openEdit(coupon)} className="text-indigo-600 hover:text-indigo-900 mr-3 inline-flex items-center">
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            <button onClick={() => deleteCoupon(coupon.id)} className="text-red-600 hover:text-red-900 inline-flex items-center">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {modal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-900">{modal.id ? 'Edit Coupon' : 'Create New Coupon'}</h3>
                            <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600">
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. SUMMER20"
                                        value={form.code}
                                        onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                        className="w-full border rounded-lg p-2.5 font-mono focus:ring-2 focus:ring-indigo-500 outline-none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Name</label>
                                    <input
                                        type="text"
                                        placeholder="Internal Name"
                                        value={form.coupon_name}
                                        onChange={(e) => setForm({ ...form, coupon_name: e.target.value })}
                                        className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                                    <select
                                        value={form.discount_type}
                                        onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                                        className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none bg-white font-medium"
                                    >
                                        <option value="percent">Percentage (%)</option>
                                        <option value="fixed_amount">Fixed Amount (₹)</option>
                                        <option value="free_shipping">Free Shipping</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                                    <input
                                        type="number"
                                        value={form.value}
                                        onChange={(e) => setForm({ ...form, value: e.target.value })}
                                        placeholder={form.discount_type === 'percent' ? '10' : '500'}
                                        className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                                        disabled={form.discount_type === 'free_shipping'}
                                        required={form.discount_type !== 'free_shipping'}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Amount (₹)</label>
                                    <input
                                        type="number"
                                        value={form.minimum_order_amount}
                                        onChange={(e) => setForm({ ...form, minimum_order_amount: e.target.value })}
                                        className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Discount (₹)</label>
                                    <input
                                        type="number"
                                        value={form.maximum_discount_amount || ''}
                                        placeholder="No Limit"
                                        onChange={(e) => setForm({ ...form, maximum_discount_amount: e.target.value })}
                                        className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Usage Limit</label>
                                    <input
                                        type="number"
                                        value={form.usage_limit || ''}
                                        placeholder="Infinity"
                                        onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
                                        className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                                    <input
                                        type="date"
                                        value={form.expiry}
                                        onChange={(e) => setForm({ ...form, expiry: e.target.value })}
                                        className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none font-medium"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="flex items-center space-x-2 pt-2">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">Coupon is active and usable</label>
                            </div>

                            <div className="flex justify-end space-x-3 mt-8">
                                <button
                                    type="button"
                                    onClick={() => setModal(null)}
                                    className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center"
                                >
                                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                    {modal.id ? 'Update Coupon' : 'Create Coupon'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// Simple X icon for close button
const X = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

export default CouponManagement;
